// 調整レイヤーと塗りつぶしレイヤーの ag-psd との相互変換 (純関数、Node テスト対象)。
// 単位と範囲は Photoshop と同じにしてあるので基本はそのまま写す。
import type { AdjustmentLayer as PsdAdjustment, LevelsAdjustmentChannel, CurvesAdjustmentChannel, VectorContent, Color, ColorStop, OpacityStop } from 'ag-psd'
import type { Adjustment, FillPaint, LevelsChannel, CurvePoints } from '@/types/document'
import { hexToRgb, rgbToHex } from '@/color/convert'

export function colorToHex(c: Color | undefined, fallback = '#000000'): string {
  if (!c) return fallback
  if ('r' in c) return rgbToHex({ r: c.r, g: c.g, b: c.b })
  if ('fr' in c) return rgbToHex({ r: c.fr * 255, g: c.fg * 255, b: c.fb * 255 })
  if ('k' in c && !('c' in c)) {
    const v = 255 - (c.k / 100) * 255
    return rgbToHex({ r: v, g: v, b: v })
  }
  return fallback
}
export function hexToColor(hex: string): Color {
  const c = hexToRgb(hex) ?? { r: 0, g: 0, b: 0 }
  return { r: c.r, g: c.g, b: c.b }
}

function levelsTo(ch: LevelsChannel): LevelsAdjustmentChannel {
  return { shadowInput: ch.inBlack, highlightInput: ch.inWhite, midtoneInput: ch.gamma, shadowOutput: ch.outBlack, highlightOutput: ch.outWhite }
}
function levelsFrom(ch: LevelsAdjustmentChannel | undefined): LevelsChannel | undefined {
  if (!ch) return undefined
  return { inBlack: ch.shadowInput, inWhite: ch.highlightInput, gamma: ch.midtoneInput, outBlack: ch.shadowOutput, outWhite: ch.highlightOutput }
}
function curveTo(pts: CurvePoints): CurvesAdjustmentChannel {
  return pts.map(p => ({ input: p[0], output: p[1] }))
}
function curveFrom(ch: CurvesAdjustmentChannel | undefined): CurvePoints | undefined {
  if (!ch) return undefined
  return ch.map(p => [p.input, p.output] as [number, number]).sort((a, b) => a[0] - b[0])
}

/** 変換できない場合は null (呼び出し側が警告を出す) */
export function adjustmentToPsd(a: Adjustment): PsdAdjustment | null {
  switch (a.kind) {
    case 'brightness-contrast': return { type: 'brightness/contrast', brightness: a.brightness, contrast: a.contrast, useLegacy: false }
    case 'levels': return { type: 'levels', rgb: levelsTo(a.rgb), ...(a.r ? { red: levelsTo(a.r) } : {}), ...(a.g ? { green: levelsTo(a.g) } : {}), ...(a.b ? { blue: levelsTo(a.b) } : {}) }
    case 'curves': return { type: 'curves', rgb: curveTo(a.rgb), ...(a.r ? { red: curveTo(a.r) } : {}), ...(a.g ? { green: curveTo(a.g) } : {}), ...(a.b ? { blue: curveTo(a.b) } : {}) }
    case 'exposure': return { type: 'exposure', exposure: a.exposure, offset: a.offset, gamma: a.gamma }
    case 'vibrance': return { type: 'vibrance', vibrance: a.vibrance, saturation: a.saturation }
    case 'color-lookup': return null
    case 'selective-color': {
      const cmyk = (q: [number, number, number, number]) => ({ c: q[0], m: q[1], y: q[2], k: q[3] })
      return { type: 'selective color', mode: a.relative ? 'relative' : 'absolute', reds: cmyk(a.ranges.reds), yellows: cmyk(a.ranges.yellows), greens: cmyk(a.ranges.greens), cyans: cmyk(a.ranges.cyans), blues: cmyk(a.ranges.blues), magentas: cmyk(a.ranges.magentas), whites: cmyk(a.ranges.whites), neutrals: cmyk(a.ranges.neutrals), blacks: cmyk(a.ranges.blacks) }
    }
    case 'hue-saturation':
      // 色彩の統一 (colorize) は ag-psd に項目が無いので master の値だけ書く
      return { type: 'hue/saturation', master: { a: 0, b: 0, c: 0, d: 0, hue: a.master.hue, saturation: a.master.saturation, lightness: a.master.lightness } }
    case 'color-balance':
      return {
        type: 'color balance',
        shadows: { cyanRed: a.shadows[0], magentaGreen: a.shadows[1], yellowBlue: a.shadows[2] },
        midtones: { cyanRed: a.midtones[0], magentaGreen: a.midtones[1], yellowBlue: a.midtones[2] },
        highlights: { cyanRed: a.highlights[0], magentaGreen: a.highlights[1], yellowBlue: a.highlights[2] },
        preserveLuminosity: a.preserveLuminosity
      }
    case 'black-white':
      return { type: 'black & white', reds: a.reds, yellows: a.yellows, greens: a.greens, cyans: a.cyans, blues: a.blues, magentas: a.magentas, useTint: a.tint, tintColor: hexToColor(a.tintHex) }
    case 'photo-filter': return { type: 'photo filter', color: hexToColor(a.hex), density: a.density, preserveLuminosity: a.preserveLuminosity }
    case 'channel-mixer':
      return {
        type: 'channel mixer',
        monochrome: a.monochrome,
        red: { red: a.r[0], green: a.r[1], blue: a.r[2], constant: a.r[3] },
        green: { red: a.g[0], green: a.g[1], blue: a.g[2], constant: a.g[3] },
        blue: { red: a.b[0], green: a.b[1], blue: a.b[2], constant: a.b[3] },
        ...(a.monochrome ? { gray: { red: a.r[0], green: a.r[1], blue: a.r[2], constant: a.r[3] } } : {})
      }
    case 'invert': return { type: 'invert' }
    case 'posterize': return { type: 'posterize', levels: a.levels }
    case 'threshold': return { type: 'threshold', level: a.level }
    case 'gradient-map':
      return {
        type: 'gradient map',
        name: 'Custom',
        gradientType: 'solid',
        reverse: a.reverse,
        dither: false,
        method: 'perceptual',
        smoothness: 4096,
        colorStops: a.stops.map(s => ({ color: hexToColor(s.hex), location: s.pos, midpoint: 0.5 })),
        opacityStops: [{ opacity: 1, location: 0, midpoint: 0.5 }, { opacity: 1, location: 1, midpoint: 0.5 }]
      }
  }
}

function stopsFrom(stops: ColorStop[] | undefined, fallback: { pos: number; hex: string }[]): { pos: number; hex: string }[] {
  if (!stops?.length) return fallback
  return stops.map(s => ({ pos: Math.max(0, Math.min(1, s.location)), hex: colorToHex(s.color) })).sort((x, y) => x.pos - y.pos)
}

/** 対応しない種類は null */
export function adjustmentFromPsd(p: PsdAdjustment): Adjustment | null {
  switch (p.type) {
    case 'brightness/contrast': return { kind: 'brightness-contrast', brightness: p.brightness ?? 0, contrast: p.contrast ?? 0 }
    case 'levels': {
      const rgb = levelsFrom(p.rgb) ?? { inBlack: 0, inWhite: 255, gamma: 1, outBlack: 0, outWhite: 255 }
      return { kind: 'levels', rgb, ...(p.red ? { r: levelsFrom(p.red) } : {}), ...(p.green ? { g: levelsFrom(p.green) } : {}), ...(p.blue ? { b: levelsFrom(p.blue) } : {}) }
    }
    case 'curves': {
      const rgb = curveFrom(p.rgb) ?? [[0, 0], [255, 255]]
      return { kind: 'curves', rgb, ...(p.red ? { r: curveFrom(p.red) } : {}), ...(p.green ? { g: curveFrom(p.green) } : {}), ...(p.blue ? { b: curveFrom(p.blue) } : {}) }
    }
    case 'exposure': return { kind: 'exposure', exposure: p.exposure ?? 0, offset: p.offset ?? 0, gamma: p.gamma ?? 1 }
    case 'vibrance': return { kind: 'vibrance', vibrance: p.vibrance ?? 0, saturation: p.saturation ?? 0 }
    case 'selective color': {
      const q = (v?: { c: number; m: number; y: number; k: number }): [number, number, number, number] => (v ? [v.c, v.m, v.y, v.k] : [0, 0, 0, 0])
      return { kind: 'selective-color', ranges: { reds: q(p.reds), yellows: q(p.yellows), greens: q(p.greens), cyans: q(p.cyans), blues: q(p.blues), magentas: q(p.magentas), whites: q(p.whites), neutrals: q(p.neutrals), blacks: q(p.blacks) }, relative: p.mode !== 'absolute' }
    }
    case 'hue/saturation':
      return { kind: 'hue-saturation', master: { hue: p.master?.hue ?? 0, saturation: p.master?.saturation ?? 0, lightness: p.master?.lightness ?? 0 }, colorize: false, colorizeHue: 0, colorizeSaturation: 25, colorizeLightness: 0 }
    case 'color balance': {
      const v = (c?: { cyanRed: number; magentaGreen: number; yellowBlue: number }): [number, number, number] => [c?.cyanRed ?? 0, c?.magentaGreen ?? 0, c?.yellowBlue ?? 0]
      return { kind: 'color-balance', shadows: v(p.shadows), midtones: v(p.midtones), highlights: v(p.highlights), preserveLuminosity: p.preserveLuminosity ?? true }
    }
    case 'black & white':
      return { kind: 'black-white', reds: p.reds ?? 40, yellows: p.yellows ?? 60, greens: p.greens ?? 40, cyans: p.cyans ?? 60, blues: p.blues ?? 20, magentas: p.magentas ?? 80, tint: !!p.useTint, tintHex: colorToHex(p.tintColor, '#e9c8a0') }
    case 'photo filter': return { kind: 'photo-filter', hex: colorToHex(p.color, '#ec8a00'), density: p.density ?? 25, preserveLuminosity: p.preserveLuminosity ?? true }
    case 'channel mixer': {
      const row = (c: { red: number; green: number; blue: number; constant: number } | undefined, d: [number, number, number, number]): [number, number, number, number] => (c ? [c.red, c.green, c.blue, c.constant] : d)
      const mono = !!p.monochrome
      return { kind: 'channel-mixer', r: row(mono ? p.gray : p.red, [100, 0, 0, 0]), g: row(p.green, [0, 100, 0, 0]), b: row(p.blue, [0, 0, 100, 0]), monochrome: mono }
    }
    case 'invert': return { kind: 'invert' }
    case 'posterize': return { kind: 'posterize', levels: p.levels ?? 4 }
    case 'threshold': return { kind: 'threshold', level: p.level ?? 128 }
    case 'gradient map':
      if (p.gradientType !== 'solid') return null
      return { kind: 'gradient-map', stops: stopsFrom(p.colorStops, [{ pos: 0, hex: '#000000' }, { pos: 1, hex: '#ffffff' }]), reverse: !!p.reverse }
    default:
      return null
  }
}

// ---------- 塗りつぶしレイヤー ----------

export function fillToPsd(f: FillPaint): VectorContent {
  if (f.kind === 'solid') return { type: 'color', color: hexToColor(f.hex) }
  if (f.kind === 'pattern') return { type: 'color', color: { r: 128, g: 128, b: 128 } }
  const colorStops: ColorStop[] = f.stops.map(s => ({ color: hexToColor(s.hex), location: s.pos, midpoint: 0.5 }))
  const opacityStops: OpacityStop[] = f.stops.map(s => ({ opacity: s.alpha, location: s.pos, midpoint: 0.5 }))
  return { type: 'solid', name: 'Custom', smoothness: 4096, colorStops, opacityStops, style: f.style, angle: f.angle, scale: f.scale, reverse: f.reverse, align: true, dither: false }
}

export function fillFromPsd(v: VectorContent): FillPaint | null {
  if (v.type === 'color') return { kind: 'solid', hex: colorToHex(v.color) }
  if (v.type === 'solid') {
    const alphaAt = (pos: number): number => {
      const os = v.opacityStops ?? []
      if (!os.length) return 1
      const sorted = [...os].sort((a, b) => a.location - b.location)
      if (pos <= sorted[0]!.location) return sorted[0]!.opacity
      const last = sorted[sorted.length - 1]!
      if (pos >= last.location) return last.opacity
      for (let i = 0; i < sorted.length - 1; i++) {
        const a = sorted[i]!, b = sorted[i + 1]!
        if (pos >= a.location && pos <= b.location) {
          const t = b.location === a.location ? 0 : (pos - a.location) / (b.location - a.location)
          return a.opacity + (b.opacity - a.opacity) * t
        }
      }
      return 1
    }
    const stops = (v.colorStops ?? []).map(s => ({ pos: Math.max(0, Math.min(1, s.location)), hex: colorToHex(s.color), alpha: alphaAt(s.location) })).sort((a, b) => a.pos - b.pos)
    if (stops.length < 2) return null
    const style = v.style ?? 'linear'
    return { kind: 'gradient', style: style === 'angle' || style === 'radial' || style === 'reflected' || style === 'diamond' ? style : 'linear', stops, angle: v.angle ?? 90, scale: v.scale ?? 1, reverse: !!v.reverse }
  }
  return null
}
